import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { DialogClose } from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { toast } from '@/components/ui/use-toast';
import { Checkbox } from '@/lib/pages/forms/components/Checkbox';

import {
  glacierPolicyApiDiscardTransactionMutation,
  glacierPolicyApiGetPolicyPermissionsQueryKey,
  glacierPolicyApiGetPolicyQueryKey,
  glacierPolicyApiGetPolicySummaryQueryKey,
} from '@/lib/heyapi/@tanstack/react-query.gen';

type DiscardForm = {
  confirm: boolean;
};

export const Discard = ({ accountId }: { accountId: string }) => {
  const qc = useQueryClient();
  const permissionsQk = glacierPolicyApiGetPolicyPermissionsQueryKey({
    path: { account_id: accountId },
  });
  const summariesQk = glacierPolicyApiGetPolicySummaryQueryKey();
  const policyQk = glacierPolicyApiGetPolicyQueryKey({
    path: {
      account_id: accountId,
    },
  });

  const navigate = useNavigate();

  const transfer = useMutation({
    ...glacierPolicyApiDiscardTransactionMutation(),
    onSuccess() {
      qc.refetchQueries({ queryKey: permissionsQk });
      qc.refetchQueries({ queryKey: summariesQk });
      qc.refetchQueries({ queryKey: policyQk });
      toast({
        title: 'Success',
        description: (
          <pre className="mt-2 w-[340px] rounded-md bg-slate-950 p-4">
            <code className="text-white">Discarded</code>
          </pre>
        ),
      });
      navigate('/');
    },
  });

  const form = useForm<DiscardForm>({
    defaultValues: {
      confirm: false,
    },
  });

  const { control, handleSubmit, watch } = form;
  const watchConfirm = watch('confirm');

  const onSubmit: SubmitHandler<DiscardForm> = () => {
    transfer.mutate({
      path: {
        account_id: accountId,
      },
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle>Discard Transaction</CardTitle>
          </CardHeader>
          <CardContent className="pb-3 pt-4">
            <Checkbox
              control={control}
              label="Discarding the transaction will remove it from view. Please confirm your intention."
              name="confirm"
              direction="reverse"
            />
          </CardContent>
          <CardFooter className="flex justify-end">
            <DialogClose asChild>
              <Button
                type="submit"
                className="bg-brand-green text-brand-0 transition hover:bg-brand-green hover:opacity-70 hover:duration-300"
                variant="destructive"
                disabled={!watchConfirm}
              >
                Submit
              </Button>
            </DialogClose>
          </CardFooter>
        </Card>
      </form>
    </Form>
  );
};
